/**
 * PD-25 ScoutIframeMountController — shell-side mount state machine.
 *
 * State diagram (plan §4.7):
 *
 *   idle
 *     │ attemptMount()
 *     ▼
 *   ┌─ pre-mount checks ─┐
 *   │                    │
 *   │  origin invalid    │── audit pass + origin ok ──▶ mounting
 *   │      ▼             │                                │ iframeDidLoad()
 *   │  stale_origin      │                                ▼
 *   │  / blocked         │                          handshake_pending
 *   │                    │                                │
 *   │  audit fail        │                  receiveHandshakeAck()
 *   │      ▼             │                                │
 *   │  audit_failed      │           outcome ALLOW|DEGRADE──▶ mounted_ready
 *   │  (fallback_path)   │           outcome BLOCK|WARN ──▶ handshake_block
 *   │  / fallback        │
 *   └────────────────────┘
 *
 *   Any state → unmount() → idle
 *
 * Pure state model — the React `<ScoutIframeMount>` component reads state +
 * drives transitions via these methods. All external services (H4 audit, M2
 * origin store, M3 handshake store) are INJECTED so the controller is unit-test
 * deterministic without a DOM.
 */
import {
  decidePostMount,
  decidePreMount,
} from '../scout-iframe-audit/decision';
import type { AuditDecision, CheckResult } from '../scout-iframe-audit/types';
import type { CookieAuditResultPayload } from '../scout-iframe-audit/types';
import type { ScoutOriginRotationStore } from '../scout-origin-rotation';
import {
  BridgeHandshakeStore,
  buildShellHandshakeRequest,
} from '../../bridge/handshake';
import type {
  HandshakeAckPayload,
  HandshakeOutcome,
  NegotiationResult,
} from '../../bridge/handshake';
import {
  OriginValidationResult,
  ScoutIframeMountAudit,
  ScoutIframeMountInputs,
  ScoutIframeMountState,
  ScoutIframeMountStateName,
} from './types';

/** Injectable H4 pre-mount checks runner (defined as callable so tests can stub). */
export type RunPreMountChecks = () => ReadonlyArray<CheckResult>;

/** Injectable M2 origin store accessor — gives the controller current state without
 *  coupling to the React lifecycle. */
export type OriginStoreAccessor = () => Pick<
  ReturnType<ScoutOriginRotationStore['currentState']>,
  'primary_origin' | 'secondary_origin' | 'rotation_state' | 'rotation_window'
>;

export interface ScoutIframeMountControllerOptions {
  /** Runs the 5 H4 pre-mount checks. Real impl calls H4 probes; tests inject. */
  runPreMountChecks: RunPreMountChecks;
  /** Reads current M2 rotation state without holding a React ref. */
  readOriginState?: OriginStoreAccessor;
  /** M3 handshake store; created externally so multiple mount attempts share it. */
  handshakeStore: BridgeHandshakeStore;
  /** Audit-callback fired on every state transition. */
  on_audit?: (audit: ScoutIframeMountAudit) => void;
  /** Deterministic clock for tests. */
  clock?: () => number;
}

const INITIAL: ScoutIframeMountState = {
  name: 'idle',
  fallback_path: null,
  reason_code: null,
  user_visible_reason: null,
  negotiation: null,
  outcome: null,
  at: 0,
};

const OUTCOME_TO_TERMINAL: Record<
  HandshakeOutcome,
  ScoutIframeMountStateName
> = {
  ALLOW: 'mounted_ready',
  DEGRADE: 'mounted_ready',
  WARN: 'handshake_block',
  BLOCK: 'handshake_block',
};

export class ScoutIframeMountController {
  private state: ScoutIframeMountState = { ...INITIAL };
  private inputs: ScoutIframeMountInputs | null = null;
  private readonly runPreMountChecks: RunPreMountChecks;
  private readonly readOriginState: OriginStoreAccessor | undefined;
  private readonly handshakeStore: BridgeHandshakeStore;
  private readonly on_audit: ((a: ScoutIframeMountAudit) => void) | undefined;
  private readonly clock: () => number;

  constructor(opts: ScoutIframeMountControllerOptions) {
    this.runPreMountChecks = opts.runPreMountChecks;
    this.readOriginState = opts.readOriginState;
    this.handshakeStore = opts.handshakeStore;
    this.on_audit = opts.on_audit;
    this.clock = opts.clock ?? Date.now;
  }

  current(): Readonly<ScoutIframeMountState> {
    return this.state;
  }

  /** Validate a candidate src URL against the allow-list (mirrors M2 §6.3). */
  validateOrigin(src_url: string, allowed_origin_suffixes: ReadonlyArray<string>): OriginValidationResult {
    if (!src_url) return { ok: false, reason: 'empty' };
    let parsed: URL;
    try {
      parsed = new URL(src_url);
    } catch (e) {
      return { ok: false, reason: 'parse_failed', detail: String(e) };
    }
    // Allow file:// + about:blank for controlled-fixture mode (tests).
    if (parsed.protocol === 'file:' || src_url === 'about:blank') {
      return { ok: true };
    }
    if (parsed.protocol !== 'https:') {
      return {
        ok: false,
        reason: 'not_https',
        detail: `scheme must be https; got ${parsed.protocol}`,
      };
    }
    const host = parsed.hostname;
    const allowed = allowed_origin_suffixes.some((s) => host.endsWith(s));
    if (!allowed) {
      return {
        ok: false,
        reason: 'suffix_not_allowed',
        detail: `host ${host} not in allow-list ${allowed_origin_suffixes.join(',')}`,
      };
    }
    return { ok: true };
  }

  /** Entry point: attempt mount. Returns the resulting state synchronously.
   *  Caller must NOT attach an iframe element if `name !== 'mounting'`. */
  attemptMount(inputs: ScoutIframeMountInputs): ScoutIframeMountState {
    this.inputs = inputs;
    this.emit('pd25.mount_attempt_started');

    // 1. M2 stale-origin check — refuse mount if rotation window is open and
    //    the requested src does not match the new primary.
    if (this.readOriginState !== undefined) {
      const originState = this.readOriginState();
      if (
        originState.rotation_state === 'blocked_mount_stale_origin' ||
        (originState.rotation_window && originState.primary_origin !== null &&
          !inputs.src_url.startsWith(originState.primary_origin))
      ) {
        this.state = {
          name: 'stale_origin',
          fallback_path: 'F4',
          reason_code: 'M2_STALE_ORIGIN',
          user_visible_reason:
            'Scout temporarily unavailable while origin rotation completes.',
          negotiation: null,
          outcome: null,
          at: this.clock(),
        };
        this.emit('pd25.stale_origin_detected', this.state.user_visible_reason ?? undefined);
        return this.state;
      }
    }

    // 2. Origin schema validation.
    const v = this.validateOrigin(inputs.src_url, inputs.allowed_origin_suffixes);
    if (!v.ok) {
      this.state = {
        name: 'blocked',
        fallback_path: 'F2',
        reason_code: `ORIGIN_${v.reason?.toUpperCase() ?? 'INVALID'}`,
        user_visible_reason: 'Scout is temporarily unavailable. Operations notified.',
        negotiation: null,
        outcome: null,
        at: this.clock(),
      };
      this.emit('pd25.fallback_selected', v.detail);
      return this.state;
    }

    // 3. H4 pre-mount audit.
    const checks = this.runPreMountChecks();
    const decision: AuditDecision = decidePreMount(checks);

    if (decision.verdict === 'allow') {
      this.state = {
        name: 'mounting',
        fallback_path: null,
        reason_code: decision.reason_code,
        user_visible_reason: decision.user_visible_reason,
        negotiation: null,
        outcome: null,
        at: this.clock(),
      };
      this.emit('pd25.iframe_attached');
      return this.state;
    }

    if (decision.verdict === 'block') {
      this.state = {
        name: 'audit_failed',
        fallback_path: decision.fallback_path ?? 'F2',
        reason_code: decision.reason_code,
        user_visible_reason: decision.user_visible_reason,
        negotiation: null,
        outcome: null,
        at: this.clock(),
      };
      this.emit('pd25.pre_mount_audit_failed', decision.reason_code);
      return this.state;
    }

    // fallback / warn / defer_retry — generic fallback state.
    this.state = {
      name: 'fallback',
      fallback_path: decision.fallback_path ?? 'F3',
      reason_code: decision.reason_code,
      user_visible_reason: decision.user_visible_reason,
      negotiation: null,
      outcome: null,
      at: this.clock(),
    };
    this.emit('pd25.fallback_selected', decision.reason_code);
    return this.state;
  }

  /** Called when the iframe's `load` event fires. Transitions to handshake_pending
   *  and emits a handshake request. */
  iframeDidLoad(): ScoutIframeMountState {
    if (this.state.name !== 'mounting' || this.inputs === null) {
      throw new ScoutIframeMountError(
        'INVALID_TRANSITION',
        `cannot iframeDidLoad from state=${this.state.name}`,
      );
    }
    const req = buildShellHandshakeRequest({
      iframe_mount_id: this.inputs.iframe_mount_id,
      request_id: this.inputs.handshake_request_id,
      hmac: this.inputs.handshake_hmac,
    });
    this.handshakeStore.startHandshake(req);
    this.state = {
      ...this.state,
      name: 'handshake_pending',
      at: this.clock(),
    };
    this.emit('pd25.handshake_sent', req.request_id);
    return this.state;
  }

  /** Called when an ack message arrives from the iframe. */
  receiveHandshakeAck(ack: HandshakeAckPayload): ScoutIframeMountState {
    if (this.state.name !== 'handshake_pending') {
      throw new ScoutIframeMountError(
        'INVALID_TRANSITION',
        `cannot receive ack from state=${this.state.name}`,
      );
    }
    const result = this.handshakeStore.receiveAck(ack);
    return this.applyNegotiation(result);
  }

  /** Called when the 5s handshake timeout fires. */
  reportHandshakeTimeout(): ScoutIframeMountState {
    if (this.state.name !== 'handshake_pending') {
      throw new ScoutIframeMountError(
        'INVALID_TRANSITION',
        `cannot timeout from state=${this.state.name}`,
      );
    }
    const result = this.handshakeStore.reportTimeout();
    return this.applyNegotiation(result);
  }

  /** Called when post-mount H4 ping returns OR times out. */
  reportPostMountAudit(args: { ping: CookieAuditResultPayload | 'timeout'; user_match: boolean }): ScoutIframeMountState {
    if (this.state.name !== 'mounted_ready') {
      throw new ScoutIframeMountError(
        'INVALID_TRANSITION',
        `cannot post-mount-audit from state=${this.state.name}`,
      );
    }
    const decision = decidePostMount(args);
    if (decision.verdict === 'allow') {
      return this.state;
    }
    this.state = {
      ...this.state,
      name: decision.verdict === 'block' ? 'audit_failed' : 'fallback',
      fallback_path: decision.fallback_path ?? 'F6',
      reason_code: decision.reason_code,
      user_visible_reason: decision.user_visible_reason,
      at: this.clock(),
    };
    this.emit('pd25.fallback_selected', decision.reason_code);
    return this.state;
  }

  /** Unmount + reset. */
  unmount(): void {
    this.emit('pd25.mount_unmounted');
    this.state = { ...INITIAL, at: this.clock() };
    this.inputs = null;
  }

  // ----------------------------------------- internals

  private applyNegotiation(result: NegotiationResult): ScoutIframeMountState {
    const nextName = OUTCOME_TO_TERMINAL[result.outcome];
    this.state = {
      ...this.state,
      name: nextName,
      fallback_path: result.fallback_path ?? null,
      reason_code: result.reason.split(':')[0] || null,
      user_visible_reason: result.reason,
      negotiation: result,
      outcome: result.outcome,
      at: this.clock(),
    };
    if (nextName === 'mounted_ready') {
      this.emit('pd25.handshake_acked', result.reason);
      this.emit('pd25.mounted_ready', result.outcome);
    } else {
      this.emit('pd25.handshake_blocked', result.reason);
    }
    return this.state;
  }

  private emit(event: ScoutIframeMountAudit['event'], detail?: string): void {
    if (this.on_audit === undefined) return;
    this.on_audit({ event, detail, at: this.clock() });
  }
}

export class ScoutIframeMountError extends Error {
  constructor(
    public readonly code: 'INVALID_TRANSITION',
    message: string,
  ) {
    super(message);
    this.name = 'ScoutIframeMountError';
  }
}
